import {
    BarElement,
    CategoryScale,
    Chart as ChartJS,
    Legend,
    LineElement,
    LinearScale,
    PointElement,
    TimeScale,
    Title,
    Tooltip,
} from 'chart.js'
import 'chartjs-adapter-moment'
import moment from 'moment'
import { Bar, Line } from 'react-chartjs-2'
import { twMerge } from 'tailwind-merge'

ChartJS.register(BarElement, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, TimeScale)

/**
 * Shared options for the daily-history line charts on the Team Tools dashboard.
 *
 * The three charts on that page (registrations, downloads, active players) each carried their own
 * copy of this config, and each had the same two faults on a narrow screen:
 *
 * - `maintainAspectRatio` was left at its default, so Chart.js derived the canvas height from its
 *   width. The `max-h-40` on the canvas then clipped whatever it computed, squeezing the plot into
 *   a strip beneath its own axis labels. Height belongs to the wrapper, so the ratio is off.
 * - Every day got a tick. Fourteen dates across ~300px overlap into a smear, which Chart.js
 *   then rotates to try to rescue.
 */
const timeSeriesOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index' as const, intersect: false },
    plugins: {
        legend: {
            position: 'bottom' as const,
            labels: { boxWidth: 12, boxHeight: 12, padding: 12, usePointStyle: true },
        },
        tooltip: { mode: 'index' as const },
    },
    elements: {
        point: { radius: 2, hitRadius: 12 },
        line: { borderWidth: 2, tension: 0.25 },
    },
    scales: {
        x: {
            type: 'time' as const,
            time: { unit: 'day' as const, tooltipFormat: moment.localeData().longDateFormat('L') },
            ticks: { autoSkip: true, maxTicksLimit: 6, maxRotation: 0, minRotation: 0, font: { size: 10 } },
            grid: { display: false },
        },
        y: {
            min: 0,
            ticks: { precision: 0, maxTicksLimit: 5, font: { size: 10 } },
            grid: { color: 'rgba(255,255,255,0.06)' },
        },
    },
}

/**
 * A daily-history line chart that fills its box at any width.
 *
 * The wrapper owns the height; pass `className` to change it. Anything in `options` is merged over
 * the shared defaults at the top level, which is enough for the callers here — none of them need to
 * override a nested scale.
 */
const TimeSeries = ({ data, className, options }: { data: any; className?: string; options?: object }) => (
    <div className={twMerge('relative h-56 w-full sm:h-64', className)}>
        <Line
            options={{
                ...timeSeriesOptions,
                ...(options ?? {}),
                // `scales` merges one level deeper than the rest. A caller adding a second
                // y-axis (store sales plots dollars and points together, which are orders of
                // magnitude apart) would otherwise have to re-declare the x time-scale to keep
                // it, and that duplicated config is exactly what this file consolidated.
                scales: { ...timeSeriesOptions.scales, ...((options as any)?.scales ?? {}) },
                // `plugins` likewise: a caller adding tooltip callbacks would
                // otherwise drop the shared legend layout without meaning to.
                plugins: { ...timeSeriesOptions.plugins, ...((options as any)?.plugins ?? {}) },
            }}
            data={data}
        />
    </div>
)

/**
 * A daily-history key as the calendar day it names, e.g. "2026-09-15".
 *
 * The backend counts each chart day in the zone the "times in" picker names and
 * keys it by that day's local midnight, such as "2026-09-15 00:00:00-04:00".
 * Handed to the time axis as-is, that is an instant, and the browser draws an
 * instant at the device's own time: for anybody west of the zone it lands on the
 * evening before, so every point was drawn a day early. A bare date is parsed as
 * local midnight on whatever device reads it, so it is drawn on the day it is.
 */
export const dayLabel = (key: string): string => String(key).slice(0, 10)

export const Chart = {
    Bar,
    Line,
    TimeSeries,
}
